/**
 * LabWalletCard — lets a lab admin see their EFGH pre-paid wallet balance,
 * top it up via Razorpay (with configurable bonus packs), and browse recent
 * transactions.
 *
 * Wallet debits happen when marketplace/follow-up bookings settle directly
 * to the lab's UPI (EFGH can't skim commission at payment time, so we
 * subtract from the pre-paid balance instead).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Wallet, Plus, Loader2, ArrowUp, ArrowDown, Sparkles, Info } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  topup: "Razorpay top-up",
  topup_bonus: "Top-up bonus",
  subscription_bonus: "Subscription bonus",
  admin_adjust: "Admin adjustment",
  commission_debit: "Commission",
  refund: "Refund",
};

export default function LabWalletCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [packs, setPacks] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, p] = await Promise.all([
        api.get("/payments/lab-wallet"),
        api.get("/public/platform-settings"),
      ]);
      setData(w.data);
      setPacks(p.data?.pricing?.lab_wallet_topup_packs || []);
    } catch (e) { /* silent — dashboard mustn't break */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Card className="mt-4 p-5 rounded-2xl"><Loader2 className="w-4 h-4 animate-spin text-ink/40" /></Card>;
  const balance = Number(data?.balance_rupees || 0);
  const isLow = balance < 500;

  return (
    <>
      <Card
        className={`mt-4 p-5 rounded-2xl ${isLow ? "border-amber-300 bg-amber-50/60" : ""}`}
        data-testid="lab-wallet-card"
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink/50 font-heading font-semibold flex items-center gap-1.5">
              <Wallet className="w-3.5 h-3.5" /> EFGH lab wallet
            </div>
            <div className="mt-2 font-heading font-bold text-3xl text-ink" data-testid="lab-wallet-balance">
              ₹{balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="mt-1 text-xs text-ink/60 max-w-md flex items-start gap-1.5">
              <Info className="w-3 h-3 mt-0.5 shrink-0 text-ink/40" />
              Booking payments settle directly to your UPI. EFGH deducts commission from this pre-paid balance. Keep at least ₹500 to avoid service interruption.
            </div>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="bg-efgh-orange hover:bg-efgh-orange/90 text-white" data-testid="lab-wallet-recharge">
            <Plus className="w-4 h-4 mr-1.5" /> Recharge wallet
          </Button>
        </div>

        {isLow && (
          <div className="mt-4 p-3 rounded-xl bg-amber-100 border border-amber-200 text-amber-800 text-xs" data-testid="lab-wallet-low">
            <b>Low balance</b> — recharge now to keep marketplace and follow-up bookings running smoothly.
          </div>
        )}

        {/* Recent transactions */}
        {data?.transactions?.length > 0 && (
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-ink/50 font-heading font-semibold mb-2">Recent activity</div>
            <div className="space-y-1.5">
              {data.transactions.slice(0, 6).map((t) => (
                <div key={t.id} className="flex items-center justify-between text-sm py-1.5 border-b border-border last:border-0" data-testid={`lw-txn-${t.id}`}>
                  <div className="flex items-center gap-2">
                    {t.direction === "credit"
                      ? <ArrowUp className="w-3.5 h-3.5 text-efgh-green" />
                      : <ArrowDown className="w-3.5 h-3.5 text-danger" />}
                    <div>
                      <div className="text-ink text-sm">{KIND_LABEL[t.kind] || t.kind}</div>
                      {t.note && <div className="text-[11px] text-ink/50">{t.note}</div>}
                    </div>
                  </div>
                  <div className={`font-heading font-semibold ${t.direction === "credit" ? "text-efgh-green" : "text-ink"}`}>
                    {t.direction === "credit" ? "+" : "−"}₹{Number(t.amount).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <LabWalletTopupDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        packs={packs}
        user={user}
        onSuccess={() => { setDialogOpen(false); load(); }}
      />
    </>
  );
}

function LabWalletTopupDialog({ open, onClose, packs, user, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [selectedPack, setSelectedPack] = useState(null);
  const [busy, setBusy] = useState(false);

  const effectiveAmount = useMemo(() => selectedPack?.amount ?? Number(amount || 0), [selectedPack, amount]);
  const effectiveBonus = useMemo(() => {
    if (selectedPack) return selectedPack.bonus || 0;
    // Custom amount — no bonus
    return 0;
  }, [selectedPack]);

  const recharge = async () => {
    if (effectiveAmount < 100) { toast.error("Minimum top-up is ₹100"); return; }
    setBusy(true);
    try {
      const body = { amount_rupees: effectiveAmount };
      if (selectedPack) body.pack_id = selectedPack.id;
      const { data: order } = await api.post("/payments/lab-wallet/topup-order", body);
      const result = await openRazorpayCheckout({
        key_id: order.key_id,
        order_id: order.order_id,
        amount_paise: order.amount_paise,
        description: `EFGH lab wallet · ₹${effectiveAmount}${effectiveBonus ? ` + ₹${effectiveBonus} bonus` : ""}`,
        prefill: { name: user?.full_name || "", email: user?.email || "", contact: user?.phone || "9876543210" },
        theme_color: "#F97316",
      });
      if (!result) { setBusy(false); return; }
      const v = await api.post("/payments/verify", {
        razorpay_order_id: result.razorpay_order_id,
        razorpay_payment_id: result.razorpay_payment_id,
        razorpay_signature: result.razorpay_signature,
      });
      const msg = v.data.bonus > 0
        ? `Wallet credited ₹${v.data.rupees} + ₹${v.data.bonus} bonus 🎉`
        : `Wallet credited ₹${v.data.rupees}`;
      toast.success(msg);
      onSuccess();
    } catch (e) {
      toast.error(apiError(e, "Recharge failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="lab-wallet-topup-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet className="w-5 h-5 text-efgh-orange" /> Recharge lab wallet</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 mt-2" data-testid="lw-packs">
          {packs.map((p) => (
            <button
              key={p.id}
              onClick={() => { setSelectedPack(p); setAmount(""); }}
              data-testid={`lw-pack-${p.id}`}
              className={`p-3 rounded-xl border text-left transition-all ${selectedPack?.id === p.id ? "border-efgh-orange bg-efgh-orange/5 ring-2 ring-efgh-orange/20" : "border-border hover:border-ink/20"}`}
            >
              {p.popular && <span className="absolute -mt-5 ml-16 text-[9px] bg-efgh-orange text-white px-1.5 py-0.5 rounded-full font-bold uppercase tracking-widest">Best</span>}
              <div className="text-[10px] uppercase tracking-widest text-ink/50 font-semibold">{p.label}</div>
              <div className="mt-1 font-heading font-bold text-xl text-ink">₹{p.amount.toLocaleString()}</div>
              {p.bonus > 0 && (
                <div className="mt-0.5 text-[11px] text-efgh-orange font-semibold flex items-center gap-0.5"><Sparkles className="w-2.5 h-2.5" /> +₹{p.bonus} bonus</div>
              )}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-ink/50 font-heading font-semibold mb-1.5">Or enter custom amount</div>
          <Input
            type="number" min="100" step="100"
            placeholder="₹ Custom amount"
            value={amount}
            data-testid="lw-custom-amount"
            onChange={(e) => { setAmount(e.target.value); setSelectedPack(null); }}
          />
        </div>

        <div className="mt-4 p-3 rounded-xl bg-muted/50 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-ink/70">You pay</span>
            <span className="font-heading font-semibold text-ink">₹{effectiveAmount.toLocaleString()}</span>
          </div>
          {effectiveBonus > 0 && (
            <div className="flex items-center justify-between mt-1">
              <span className="text-efgh-orange">Bonus credit</span>
              <span className="font-heading font-semibold text-efgh-orange">+ ₹{effectiveBonus.toLocaleString()}</span>
            </div>
          )}
          <div className="mt-2 pt-2 border-t border-border flex items-center justify-between">
            <span className="text-ink/70">Wallet credit total</span>
            <span className="font-heading font-bold text-lg text-ink">₹{(effectiveAmount + effectiveBonus).toLocaleString()}</span>
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={recharge} disabled={busy || effectiveAmount < 100} className="bg-efgh-orange hover:bg-efgh-orange/90 text-white" data-testid="lw-recharge-confirm">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening Razorpay…</> : `Pay ₹${effectiveAmount.toLocaleString()}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
